import { useMemo, useState } from 'react'
import FileSaver from 'file-saver'
import { getOlympiadsFileService } from '@gr-monorepo/common/public/utils/FileService/OlympiadsFileService'
import { useClickStreamContext } from 'features/shared/ClickStream'
import type { OlympiadEducationYear } from '../../constants/years'

export type LevelId = 'regions' | 'municipalities' | 'schools'

export enum BtnState {
  Loading = 'loading',
  Default = 'default',
  Error = 'error',
}

const initialLevelStates: Record<LevelId, BtnState> = {
  regions: BtnState.Default,
  municipalities: BtnState.Default,
  schools: BtnState.Default,
}

export const useOlympiadStatisticDownload = (olympiadId: string | undefined, years: OlympiadEducationYear) => {
  const { logPageEvent } = useClickStreamContext()
  const [levelStates, setLevelStates] = useState(initialLevelStates)
  const [showError, setShowError] = useState(false)
  const fileService = useMemo(() => getOlympiadsFileService(years.map(Number)), [years])

  const setLevelState = (level: LevelId, state: BtnState) => {
    setLevelStates((prev) => ({ ...prev, [level]: state }))
  }

  const onLevelClick = async (level: LevelId) => {
    if (!olympiadId || !fileService) return

    setLevelState(level, BtnState.Loading)

    const fileName = `${level}.xlsx`
    const file = await fileService.getOlympiadStatisticRussiaFile(olympiadId, level)
    const success = !!file

    if (file) {
      FileSaver.saveAs(
        new Blob([file], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        fileName,
      )
    }

    if (success) {
      logPageEvent?.({
        eventType: 'report_download_success',
        eventInfo: 'report_download_success',
        cd1: olympiadId,
        cd2: level,
      })
      setLevelState(level, BtnState.Default)
      return
    }

    logPageEvent?.({
      eventType: 'report_download_error',
      eventInfo: 'report_download_error',
      cd1: olympiadId,
      cd2: level,
      cd3: 'file_not_found',
    })
    setShowError(true)
    setLevelState(level, BtnState.Error)
  }

  return {
    levelStates,
    showError,
    setShowError,
    onLevelClick,
  }
}
